import { useState } from "react";
import { MapPin, Pin, ShieldCheck } from "lucide-react";
import { useNearestSafeZone } from "@/lib/safeZones";
import { formatDistance } from "@/lib/radar";
import { useI18n } from "@/lib/I18nContext";

// Fallback English copy — used if the translation key is missing. Real
// text lives in i18n.js under `chat.safety_banner`.
const SAFETY_COPY_FALLBACK =
  "For your safety, always trade in an Official Safe Zone. Look at the sponsored locations below!";

export default function ChatSafetyHeader() {
  const { t } = useI18n();
  const { data: zone, isLoading, isError } = useNearestSafeZone();
  const safetyText = t("chat.safety_banner", SAFETY_COPY_FALLBACK);

  return (
    <div
      data-testid="chat-safety-stack"
      className="space-y-3 -mt-1 mb-3 scroll-mt-2"
    >
      {/* 2a. Safety banner */}
      <section
        data-testid="chat-safety-banner"
        className="relative rounded-lg bg-[#0A0A0A] border border-white/10 border-t-2 border-t-amber-400 px-4 py-3 flex items-start gap-3"
      >
        <ShieldCheck className="h-5 w-5 text-[#E5B83A] shrink-0 mt-0.5" />
        <p className="flex-1 font-body text-sm text-white leading-snug pr-7">
          {safetyText}
        </p>
        <span
          aria-label="Pinned"
          className="absolute top-2 right-2 text-[#A1A1AA]/70 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-body"
        >
          <Pin className="h-3.5 w-3.5" />
          Pinned
        </span>
      </section>

      {/* 2b. Sponsored safe zone card */}
      {isLoading ? (
        <SafeZoneSkeleton />
      ) : isError || !zone ? null : (
        <SafeZoneCard zone={zone} />
      )}
    </div>
  );
}

function SafeZoneSkeleton() {
  return (
    <section
      data-testid="chat-safe-zone-card-skeleton"
      className="rounded-lg bg-[#0A0A0A] border border-amber-500/30 overflow-hidden"
    >
      <div className="aspect-[16/9] bg-white/5 animate-pulse" />
      <div className="p-4 space-y-2">
        <div className="h-4 w-2/3 bg-white/5 rounded animate-pulse" />
        <div className="h-3 w-1/2 bg-white/5 rounded animate-pulse" />
        <div className="h-3 w-3/4 bg-white/5 rounded animate-pulse" />
      </div>
    </section>
  );
}

function SafeZoneCard({ zone }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showDistance =
    zone.is_default_fallback === false &&
    typeof zone.distance_meters === "number";

  return (
    <section
      data-testid="chat-safe-zone-card"
      className="relative rounded-lg bg-[#0A0A0A] border border-amber-500/30 overflow-hidden shadow-[0_0_30px_-15px_rgba(229,184,58,0.35)]"
    >
      {/* SPONSORED pill */}
      <span
        data-testid="chat-safe-zone-sponsored-pill"
        className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-[0.18em] font-body text-[#E5B83A] bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md border border-amber-500/30"
      >
        Sponsored
      </span>

      {/* Image */}
      <div className="aspect-[16/9] w-full bg-[#0a0a0a] relative max-h-48">
        {imgFailed || !zone.image_url ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#121212]">
            <MapPin className="h-8 w-8 text-[#E5B83A]/40" />
          </div>
        ) : (
          <img
            src={zone.image_url}
            alt={zone.name}
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover"
            draggable={false}
          />
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-1.5">
        <h3
          data-testid="chat-safe-zone-name"
          className="font-display text-base font-bold text-[#E5B83A] leading-tight"
        >
          {zone.name}
        </h3>
        <div
          data-testid="chat-safe-zone-address"
          className="flex items-center gap-1.5 text-xs font-body text-[#A1A1AA]"
        >
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{zone.address}</span>
        </div>
        {showDistance ? (
          <p
            data-testid="chat-safe-zone-distance"
            className="text-xs font-body text-[#A1A1AA]"
          >
            {formatDistance(zone.distance_meters)} away
          </p>
        ) : null}
        {zone.coupon_text ? (
          <p
            data-testid="chat-safe-zone-coupon"
            className="font-body italic text-xs text-[#A1A1AA]/80 leading-relaxed pt-1.5"
          >
            {zone.coupon_text}
          </p>
        ) : null}
      </div>
    </section>
  );
}
